export const TAU = Math.PI * 2

export function clamp(v: number, min: number, max: number): number {
  return v < min ? min : v > max ? max : v
}

export function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t
}

export function invLerp(a: number, b: number, v: number): number {
  return a === b ? 0 : (v - a) / (b - a)
}

/**
 * Framerate-independent exponential approach. `rate` is "how much of the gap is
 * closed per second" expressed as a decay constant — higher means snappier.
 */
export function damp(current: number, target: number, rate: number, dt: number): number {
  return lerp(current, target, 1 - Math.exp(-rate * dt))
}

/** Shortest signed difference between two angles, in (-PI, PI]. */
export function angleDelta(from: number, to: number): number {
  let d = (to - from) % TAU
  if (d > Math.PI) d -= TAU
  if (d < -Math.PI) d += TAU
  return d
}

export function angleLerp(from: number, to: number, t: number): number {
  return from + angleDelta(from, to) * t
}

export function angleDamp(from: number, to: number, rate: number, dt: number): number {
  return from + angleDelta(from, to) * (1 - Math.exp(-rate * dt))
}

export function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = clamp01(invLerp(edge0, edge1, x))
  return t * t * (3 - 2 * t)
}

export const easeOutCubic = (t: number): number => 1 - Math.pow(1 - t, 3)
export const easeOutQuint = (t: number): number => 1 - Math.pow(1 - t, 5)
export const easeInQuad = (t: number): number => t * t
export const easeInOutQuad = (t: number): number =>
  t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2

export function moveTowards(current: number, target: number, maxDelta: number): number {
  const d = target - current
  if (Math.abs(d) <= maxDelta) return target
  return current + Math.sign(d) * maxDelta
}
